import { SearchOutlined } from '@mui/icons-material';
import { InputAdornment, ListSubheader } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { useEffectAsync } from '../../reactHelper';

const SearchSelect = ({
  label,
  fullWidth,
  multiple,
  value,
  emptyValue = 0,
  emptyTitle = '\u00a0',
  onChange,
  endpoint,
  data,
  keyGetter = (item) => item.id,
  titleGetter = (item) => item.name,
  dataGetter = (data) => data,
  debounceDelay = 300,
}) => {
  const t = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [endpointItems, setEndpointItems] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceDelay);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [debouncedQuery]);

  // Solo fetch cuando viene endpoint — data se usa directo, sin pasar por estado
  useEffectAsync(async () => {
    if (endpoint) {
      const response = await fetch(endpoint);
      if (response.ok) {
        setEndpointItems(dataGetter(await response.json()));
      } else {
        throw Error(await response.text());
      }
    }
  }, []);

  // Si viene prop data, úsala directo (mismo render, sin retraso)
  // Si viene endpoint, usa el estado interno
  const sourceItems = data ?? endpointItems;

  const filteredOptions = (sourceItems || []).filter(
    (option) => option && option.name.toLowerCase().includes(debouncedQuery.toLowerCase())
  );

  return (
    <FormControl fullWidth={fullWidth}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value ?? ''} onChange={onChange} multiple={multiple}>
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder={t('sharedSearch')}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            value={searchQuery}
            inputRef={inputRef}
          />
        </ListSubheader>
        {!multiple && emptyValue !== null && (
          <MenuItem value={emptyValue}>{emptyTitle}</MenuItem>
        )}
        {filteredOptions.map((option) => (
          <MenuItem key={keyGetter(option)} value={keyGetter(option)}>
            {titleGetter(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SearchSelect;